import { createJSONStorage, persist, type PersistOptions } from 'zustand/middleware';
import type { StateCreator, StoreApi } from 'zustand';
import { subscribeStorageUserId, userScopedStorage } from './userScopedStorage';

const rehydrateThunks = new Set<() => void>();

subscribeStorageUserId(() => {
  for (const rehydrate of rehydrateThunks) {
    rehydrate();
  }
});

/**
 * Zustand `persist` middleware that namespaces storage per signed-in user via {@link userScopedStorage}.
 * Rehydrates all registered stores when the storage user id changes. Do not pass custom `storage` or `merge`;
 * `name`, `partialize`, `version`, and `migrate` behave like the stock `persist` middleware.
 */
export const persistForUser: typeof persist = (<T, U>(
  initializer: StateCreator<T, [['zustand/persist', unknown]], []>,
  options: PersistOptions<T, U>,
) => {
  let storeApi: StoreApi<T> | undefined;
  const partialize = options.partialize ?? ((state: T) => state as unknown as U);

  return persist<T, [], [], U>(
    (set, get, api) => {
      storeApi = api;
      rehydrateThunks.add(() => {
        void api.persist.rehydrate();
      });
      return initializer(set, get, api);
    },
    {
      ...options,
      storage: createJSONStorage(() => userScopedStorage),
      merge: (persistedState, currentState) => ({
        ...currentState,
        ...(storeApi ? partialize(storeApi.getInitialState()) : {}),
        ...(persistedState as Partial<T> | undefined),
      }),
    },
  );
}) as unknown as typeof persist;
